// @ts-strict-ignore
import { type IconComponent, Text } from '@backend/lib-design-system';
import { BackFilledIcon, NextFilledIcon } from '@backend/lib-design-system/icons';
import Image from 'next/image';
import type { FC, MouseEvent, ReactNode } from 'react';

import { BarWrapper, LinkDiv, PageLink } from '~/components/MissionsList/MissionListPaginationBar.style';
import { useIsPromanBusinessUnit } from '~/hooks';

interface MissionsPaginationBarProps {
  pageCount: number;
  currentPage: number;
  onChangePage: (targetPage: number) => void;
  getPageHref: (targetPage: number) => string;
}

const COUNT_PAGES_IN_BAR = 5;
const COUNT_PAGES_BEFORE_SELECTED_ELT = Math.floor(COUNT_PAGES_IN_BAR / 2);

// Number displayed by the first element in the bar
const getFirstElementPageNumber = (currentPage, pageCount) => {
  if (pageCount <= COUNT_PAGES_IN_BAR) {
    return 1;
  }

  if (currentPage <= COUNT_PAGES_BEFORE_SELECTED_ELT) {
    return 1;
  }

  if (currentPage > pageCount - (COUNT_PAGES_IN_BAR - COUNT_PAGES_BEFORE_SELECTED_ELT)) {
    return pageCount - COUNT_PAGES_IN_BAR + 1;
  }

  return currentPage - COUNT_PAGES_BEFORE_SELECTED_ELT;
};

export const MissionListPaginationBar: FC<MissionsPaginationBarProps> = ({
  pageCount,
  currentPage,
  onChangePage,
  getPageHref,
}) => {
  const isProman = useIsPromanBusinessUnit();

  const isReachable = (targetPage: number) => targetPage !== currentPage && targetPage >= 1 && targetPage <= pageCount;

  // a real link, so that crawlers reach the offers listed on the next pages
  const renderPageLink = (targetPage: number, content: ReactNode) =>
    isReachable(targetPage) ? (
      <PageLink
        href={getPageHref(targetPage)}
        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
          // a modified click opens the page in a new tab or window, as on any link
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
            return;
          }
          event.preventDefault();
          onChangePage(targetPage);
        }}
      >
        {content}
      </PageLink>
    ) : (
      content
    );

  const buildPagesBar = () => {
    const countElements = Math.min(pageCount, COUNT_PAGES_IN_BAR);
    const firstPageNumber = getFirstElementPageNumber(currentPage, pageCount);

    return [...Array(countElements)].map((elt, index) => {
      const targetPage = firstPageNumber + index;
      const isSelected = currentPage === targetPage;
      return (
        <LinkDiv
          id={index.toString()}
          key={index}
          data-selected={isSelected}
          data-disabled={isSelected}
          data-proman={isProman}
        >
          {renderPageLink(targetPage, <Text variant="body">{targetPage}</Text>)}
        </LinkDiv>
      );
    });
  };

  const buildIconBar = (id: string, targetPage: number, isDisabled: boolean, IconComp: IconComponent) => (
    <LinkDiv id={id} data-disabled={isDisabled} data-proman={isProman}>
      {renderPageLink(targetPage, <IconComp size="m" aria-label={`Go to ${id}`} />)}
    </LinkDiv>
  );

  const buildImageBar = (id, targetPage, isDisabled, image) => (
    <LinkDiv id={id} data-disabled={isDisabled} data-proman={isProman}>
      {renderPageLink(
        targetPage,
        <Image src={image} width={24} height={24} aria-label={`Go to ${id}`} alt={`Navigation to ${id} page`} />,
      )}
    </LinkDiv>
  );

  return (
    <BarWrapper alignItems="center" space="s" justifyContent="center">
      {buildImageBar('first', 1, currentPage === 1, '/images/icn-fast-backward.svg')}
      {buildIconBar('previous', currentPage - 1, currentPage === 1, BackFilledIcon)}
      {buildPagesBar()}
      {buildIconBar('next', currentPage + 1, currentPage === pageCount, NextFilledIcon)}
      {buildImageBar('last', pageCount, currentPage === pageCount, '/images/icn-fast-forward.svg')}
    </BarWrapper>
  );
};
